import { useRef, type MutableRefObject, type TextareaHTMLAttributes } from "react";
import { useEmojiText, type EmojiText } from "./useEmojiText";
import { targetOf, type EmojiTarget } from "./emojiTarget";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "ref"> & {
  value: string;
  onValueChange: (value: string) => void;
  /** Limit in code points; omit when the field has none. */
  max?: number;
  /** Receives the field's emoji helper on every render (for a picker that inserts into the last focused field). */
  apiRef?: MutableRefObject<EmojiText | null>;
  /** Called when the field gains focus (a picker button that inserts into the last focused field). */
  onEmojiFocus?: (target: EmojiTarget) => void;
};

/** A textarea with emoji conversion, for places that render several such fields (the Blog composer). */
export default function EmojiTextarea({ value, onValueChange, max, apiRef, onEmojiFocus, onFocus, onCompositionStart, onCompositionEnd, onPaste, ...rest }: Props) {
  const emoji = useEmojiText(value, onValueChange, { max });
  if (apiRef) apiRef.current = emoji;
  const latest = useRef(emoji);
  latest.current = emoji;
  return (
    <textarea
      {...rest}
      ref={emoji.ref}
      value={value}
      onChange={emoji.props.onChange}
      onFocus={(e) => {
        onEmojiFocus?.(targetOf(() => latest.current));
        onFocus?.(e);
      }}
      onCompositionStart={(e) => {
        emoji.props.onCompositionStart(e);
        onCompositionStart?.(e);
      }}
      onCompositionEnd={(e) => {
        emoji.props.onCompositionEnd(e);
        onCompositionEnd?.(e);
      }}
      onPaste={(e) => {
        emoji.props.onPaste(e);
        onPaste?.(e);
      }}
    />
  );
}
